import { useState, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { UploadCloud, Loader2, CheckCircle2, AlertCircle, FileSpreadsheet, Users } from "lucide-react";
import { toast } from "sonner";

const ENDPOINTS = {
  personnel: {
    label: "Personal (CSV Onboarding)",
    icon: Users,
    accept: ".csv",
    url: "/admin/import/personnel",
    hint: "CSV Latin-1 con separador ';' — exportado de SAP-HR/SGO (SCOUTD_..._Integracion_Onboarding.csv). Los empleados se mapean al contrato por los primeros 7 dígitos del Cod. Centro de Coste.",
  },
  vehicles: {
    label: "Maquinaria (XLSX)",
    icon: FileSpreadsheet,
    accept: ".xlsx",
    url: "/admin/import/vehicles",
    hint: "Excel de maquinaria con la cabecera 'Matrícula, Bastidor, Marca, ...' en la fila 3. Los vehículos se mapean al contrato por 'Código del Centro'.",
  },
};

export default function BulkImportTab() {
  const { authAxios } = useAuth();
  const [loading, setLoading] = useState({ personnel: false, vehicles: false });
  const [result, setResult] = useState({ personnel: null, vehicles: null });
  const refs = { personnel: useRef(null), vehicles: useRef(null) };

  const upload = async (kind) => {
    const file = refs[kind].current?.files?.[0];
    if (!file) return;
    setLoading(prev => ({ ...prev, [kind]: true }));
    setResult(prev => ({ ...prev, [kind]: null }));
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await authAxios.post(ENDPOINTS[kind].url, form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 240000, // 4 min for big CSVs
      });
      setResult(prev => ({ ...prev, [kind]: data }));
      if (data.error) toast.error(data.error);
      else {
        const total = (data.added || 0) + (data.updated || 0);
        toast.success(`✓ ${total} registros procesados (${data.added || 0} nuevos, ${data.updated || 0} actualizados)`);
      }
    } catch (err) {
      console.error(err);
      setResult(prev => ({ ...prev, [kind]: { error: err.response?.data?.detail || err.message } }));
      toast.error("Error importando fichero");
    } finally {
      setLoading(prev => ({ ...prev, [kind]: false }));
      if (refs[kind].current) refs[kind].current.value = "";
    }
  };

  return (
    <div className="space-y-6" data-testid="bulk-import-tab">
      <div className="card-white p-5">
        <div className="flex items-center gap-3 mb-1">
          <UploadCloud size={22} className="text-[#007934]" />
          <h2 className="text-lg font-bold text-slate-800">Importación masiva</h2>
        </div>
        <p className="text-sm text-slate-500">
          Sube los ficheros SAP-HR o SGO para actualizar en bloque el personal y la
          maquinaria de <strong>todos los contratos existentes</strong>. El importador es
          <strong> idempotente</strong>: matchea por nombre completo + contrato (personal) y por
          matrícula (vehículos). Los registros que apunten a contratos no dados de alta
          se contabilizan y se ignoran.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {Object.entries(ENDPOINTS).map(([kind, cfg]) => (
          <div key={kind} className="card-white p-5 space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#E6F4EA] flex items-center justify-center">
                <cfg.icon size={20} className="text-[#007934]" />
              </div>
              <div>
                <h3 className="font-bold text-slate-800">{cfg.label}</h3>
                <p className="text-xs text-slate-400">{cfg.accept.toUpperCase()}</p>
              </div>
            </div>
            <p className="text-xs text-slate-500 leading-relaxed">{cfg.hint}</p>

            <input
              type="file"
              accept={cfg.accept}
              ref={refs[kind]}
              className="hidden"
              onChange={() => upload(kind)}
              data-testid={`import-${kind}-input`}
            />
            <button
              data-testid={`import-${kind}-btn`}
              onClick={() => refs[kind].current?.click()}
              disabled={loading[kind]}
              className="w-full flex items-center justify-center gap-2 bg-[#007934] hover:bg-[#005c27] text-white px-4 py-2.5 rounded-xl font-semibold text-sm disabled:opacity-50 transition-all"
            >
              {loading[kind] ? <Loader2 size={16} className="animate-spin" /> : <UploadCloud size={16} />}
              {loading[kind] ? "Procesando..." : `Seleccionar ${cfg.accept}`}
            </button>

            {result[kind] && (
              <div className={`rounded-lg p-3 text-xs space-y-1 ${result[kind].error ? "bg-red-50 border border-red-200" : "bg-emerald-50 border border-emerald-200"}`}>
                {result[kind].error ? (
                  <div className="flex items-start gap-2 text-red-700">
                    <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
                    <span>{result[kind].error}</span>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-2 text-emerald-800 font-semibold">
                      <CheckCircle2 size={14} /> Importación completada
                    </div>
                    <div className="grid grid-cols-2 gap-x-4 text-slate-700 mt-2">
                      <div><strong className="text-emerald-700">{result[kind].added || 0}</strong> nuevos</div>
                      <div><strong className="text-blue-700">{result[kind].updated || 0}</strong> actualizados</div>
                      {result[kind].skipped_no_contract > 0 && (
                        <div className="col-span-2 text-amber-700">
                          {result[kind].skipped_no_contract} filas ignoradas (contrato no existe en la plataforma)
                        </div>
                      )}
                      {result[kind].skipped_bad_plate > 0 && (
                        <div className="col-span-2 text-amber-700">
                          {result[kind].skipped_bad_plate} filas con matrícula inválida
                        </div>
                      )}
                      {result[kind].errors > 0 && (
                        <div className="col-span-2 text-red-700">{result[kind].errors} errores</div>
                      )}
                    </div>
                    {result[kind].top_unknown_codes?.length > 0 && (
                      <details className="mt-2 text-slate-500">
                        <summary className="cursor-pointer">Códigos de contrato no reconocidos</summary>
                        <ul className="mt-1 pl-3 font-mono">
                          {result[kind].top_unknown_codes.slice(0, 5).map(c => (
                            <li key={c.code}>{c.code}: {c.count} filas</li>
                          ))}
                        </ul>
                      </details>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
